
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowLeftCircle, Gem } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { motion } from 'framer-motion';

interface CustomAmountInputProps {
  onChange: (amount: number) => void;
  onCancel: () => void;
  initialValue?: number;
}

const CustomAmountInput: React.FC<CustomAmountInputProps> = ({ onChange, onCancel, initialValue = 0 }) => {
  const isMobile = useIsMobile();
  const [inputValue, setInputValue] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  
  // Update the formatted display when initialValue changes
  useEffect(() => {
    if (initialValue > 0) {
      setInputValue(formatCurrency(initialValue.toString()));
    }
  }, [initialValue]);
  
  // Format currency with Vietnamese separators
  const formatCurrency = (value: string): string => {
    // Remove all non-numeric characters
    const numericValue = value.replace(/[^\d]/g, '');
    
    if (!numericValue) return '';
    
    // Parse as number to handle conversion - use parseFloat for large numbers
    const numberValue = parseInt(numericValue, 10);
    
    // Check if number is too large
    if (numberValue > 1000000000) {
      return '1.000.000.000';
    }
    
    // Format with thousand separators using Vietnamese locale
    try {
      return numberValue.toLocaleString('vi-VN');
    } catch (error) {
      // Fallback manual formatting if toLocaleString fails
      let result = '';
      const strNum = numberValue.toString();
      for (let i = 0; i < strNum.length; i++) {
        if (i > 0 && (strNum.length - i) % 3 === 0) {
          result += '.';
        }
        result += strNum[i];
      }
      return result;
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formattedValue = formatCurrency(rawValue);
    setInputValue(formattedValue);
    
    // Convert formatted string back to number for the form data
    const numericValue = formattedValue.replace(/\./g, '');
    const numberValue = numericValue ? parseInt(numericValue) : 0;
    
    // Validate the amount
    if (numberValue > 1000000000) {
      setErrorMessage('Số tiền tối đa là 1 tỷ đồng');
    } else if (numberValue < 10000) {
      setErrorMessage('Số tiền tối thiểu là 10,000 đồng');
    } else {
      setErrorMessage(null);
    }
    
    onChange(numberValue);
  };

  // Handle keydown for special keys
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow Enter key to be treated as confirmation
    if (e.key === 'Enter') {
      e.preventDefault();
      const numericValue = inputValue.replace(/\./g, '');
      const amount = numericValue ? parseInt(numericValue) : 0;
      
      if (amount >= 10000 && amount <= 1000000000) {
        onChange(amount);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`space-y-4 ${isMobile ? 'p-4' : 'p-6'} rounded-lg border shadow-lg bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-amber-200`}
    >
      <Label className={`${isMobile ? 'text-base' : 'text-lg'} text-amber-700 flex items-center gap-2 font-medium`}>
        <Gem size={18} className="text-amber-500" />
        <span>Nhập số tiền đóng góp theo ý muốn của bạn</span>
      </Label>
      <div className="space-y-2">
        <div className="relative">
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-amber-200/30 via-yellow-200/30 to-orange-200/30 rounded-md"
            animate={{ 
              opacity: [0.3, 0.6, 0.3],
              transition: { duration: 3, repeat: Infinity }
            }}
          />
          <Input
            value={inputValue}
            placeholder="0"
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={`focus:ring-amber-500 focus:border-amber-500 bg-white/80 relative z-10 transition-all
              ${isMobile ? 'text-lg font-medium h-12' : 'text-xl font-medium h-14'} 
              ${isFocused ? 'shadow-inner shadow-amber-200' : 'shadow-sm'}
              ${errorMessage ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-amber-300'}
              rounded-lg pr-10`}
            inputMode="numeric"
            aria-label="Nhập số tiền đóng góp"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-amber-600 font-medium z-10">₫</span>
          
          {/* Animated sparkles when focused */}
          {isFocused && (
            <motion.div
              className="absolute right-10 top-1/2 transform -translate-y-1/2 z-10"
              animate={{
                opacity: [0.5, 1, 0.5],
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
              }}
            >
              <Sparkles size={16} className="text-amber-500" />
            </motion.div>
          )}
        </div>
        {errorMessage && (
          <motion.p 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-red-600 font-medium"
          >
            {errorMessage}
          </motion.p>
        )}
      </div>
      <p className="text-sm text-amber-600 italic text-center flex justify-center items-center">
        Đóng góp của bạn sẽ giúp tổ chức các hoạt động ý nghĩa!
      </p>
      <Button
        type="button"
        variant="ghost"
        className={`text-amber-600 w-full flex items-center justify-center gap-2 ${isMobile ? 'text-xs' : 'text-sm'} hover:bg-amber-100 hover:text-amber-700 transition-all duration-300`}
        onClick={onCancel}
      >
        <ArrowLeftCircle className="h-4 w-4" />
        <span>Quay lại mức đóng góp đề xuất</span>
      </Button>
    </motion.div>
  );
};

export default CustomAmountInput;
